import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const clamp = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.max(1, Math.min(120, n)) : 44;
};

export const OvertimeDefaultsCard = ({ token }) => {
  const { toast } = useToast();
  const [policy, setPolicy] = useState('standard');
  const [threshold, setThreshold] = useState(44);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/company/overtime-defaults`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setPolicy(r.data.overtime_policy_default || 'standard');
        setThreshold(r.data.overtime_weekly_threshold_default ?? 44);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const thr = clamp(threshold);
    try {
      await axios.put(`${API}/company/overtime-defaults`, {
        overtime_policy_default: policy,
        overtime_weekly_threshold_default: thr,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setThreshold(thr);
      toast({ title: 'Overtime defaults saved', description: `New staff will start at ${policy} / ${thr}h per week. Existing staff keep their own setting.` });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-lg mb-6" data-testid="overtime-defaults-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-bold flex items-center gap-2">
          <Clock className="w-5 h-5 text-orange-400" /> Overtime Defaults for New Staff
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Default overtime policy</Label>
          <Select value={policy} onValueChange={setPolicy}>
            <SelectTrigger data-testid="ot-default-policy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard" data-testid="ot-default-option-standard">Standard — overtime past weekly threshold</SelectItem>
              <SelectItem value="averaging" data-testid="ot-default-option-averaging">Averaging agreement — averaged over the pay period</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Default weekly OT threshold (hours)</Label>
          <Input
            type="number" min="1" max="120" step="0.5" className="w-40"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            onBlur={(e) => setThreshold(clamp(e.target.value))}
            data-testid="ot-default-threshold-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Type the threshold for your jurisdiction (e.g. 40 US FLSA, 44 Ontario ESA, 48 Nova Scotia). Pre-fills the Add User form; each staff member can still be changed in Manage Users. Existing staff are not changed.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="ot-default-save-btn">
          {saving ? 'Saving…' : 'Save overtime defaults'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default OvertimeDefaultsCard;
